import * as React from "react";
import { useState, useRef, useCallback, useEffect } from "react";
import { Window } from "@progress/kendo-react-dialogs";
import SignOutButton from "../signOutButton/signOutButton.component";
import "./attendConfirmation.component.scss"
import { ClassItemType } from "../../types/class/classItem.type";
import IdleTimer from "../idleTimer/idleTimer.component";
import ClassDetail from "../classItem/classDetail/classDetail.component";
import { Button } from "@progress/kendo-react-buttons";
import AttendanceReceipt2 from "../attendanceReceipt2/attendanceReceipt2.component";
import XplLoader from "../../components/layout/xplLoader/xplLoader.component";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { sendEmail } from "../../services/api/attendUserApi";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { IDLE_TIMER_TYPE } from "../../store/idleTimer/idleTimer.types";
import { selectSuccessScreenTimerTimeout } from "../../store/kioskSettings/kioskSettings.selector";
import { prepareReceipt, printReceipt } from "../../services/api/printerApi.service";
import store from "../../store/store";
import { startSuccessScreenTimer } from "../../store/idleTimer/idleTimer.reducer";

type AttendConfirmationProps = {
    classItem: ClassItemType,
    onClose?: () => void
}


const isDevelopment = () => {
    return process.env.NODE_ENV === 'development' || 
           process.env.REACT_APP_PTS_URL?.includes('localhost');
};

const AttendTitle = () => {
    return (
        <div className={"attend-title"}>
            <span className={"k-icon"} />
            Attended Successfully
        </div>
    );
}

const AttendConfirmation = (props: AttendConfirmationProps) => {
    const componentRef = useRef(null);
    const kioskSettings = useSelector((state: any) => state.kioskSettings);
    const [loading, setLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [printing, setPrinting] = useState(false);
    const user = useSelector((state: any) => state.user);
    const timerTimeout = useSelector(selectSuccessScreenTimerTimeout);

    const reactToPrintContent = useCallback(() => {
        return componentRef.current;
    }, [componentRef.current]);

    const onPrint = () => {
        setPrinting(true);
    }

    const onEmail = () => {
        setSendingEmail(true);
    }

    useEffect(() => {
        
        setPrinting(true);
        
        const timer = setTimeout(() => {
            setPrinting(false);
        }, timerTimeout || 3000); 
        
        return () => clearTimeout(timer);
    }, [timerTimeout]); 

    useEffect(() => {
        store.dispatch(startSuccessScreenTimer(timerTimeout));
    }, [timerTimeout]);

    useEffect(() => {
        if (sendingEmail) {
            setLoading(true);
            setLoadingProgress(10);
            
            const timeoutId = setTimeout(() => {
                console.warn("Email operation timed out");
                setSendingEmail(false);
                setLoading(false);
                setLoadingProgress(0);
            }, 30000); 
            
            processSendEmail().finally(() => {
                clearTimeout(timeoutId);
            });
        } else if (printing && kioskSettings.settings.printer) {
            setLoading(true);
            setLoadingProgress(0);
            
            const timeoutId = setTimeout(() => {
                console.warn("Print operation timed out");
                setPrinting(false);
                setLoading(false);
                setLoadingProgress(0);
            }, 30000);
            
            processPrintReceipt().finally(() => {
                clearTimeout(timeoutId);
            });
        }
    }, [sendingEmail, printing]);

    const processSendEmail = (): Promise<void> => {
        setLoading(true);
        setLoadingProgress(10);

        const receiptContent = reactToPrintContent();
        
        const pdfOptions = isDevelopment() ? {
            paperSize: "A4",
            margin: { top: "10mm", left: "10mm", right: "10mm", bottom: "10mm" },
            scale: 0.8 
        } : {
            paperSize: "A4"
        };

        return drawDOM(receiptContent, pdfOptions)
            .then((group) => {
                setLoadingProgress(50);
                return exportPDF(group);
            })
            .then((dataUri) => {
                setLoadingProgress(80);
                const classTime = format(new Date(props.classItem.startTime), "HH:mm a");
                return sendEmail({
                    className: props.classItem.title,
                    classTime: classTime,
                    attendeeName: user.name,
                    receiptBase64: dataUri.split(";base64,")[1]
                });
            })
            .then(() => {
                setLoadingProgress(100);
                setTimeout(() => {
                    setSendingEmail(false);
                    setLoading(false);
                    setLoadingProgress(0);
                }, 200); 
            })
            .catch((error) => {
                console.error("Email error:", error);
                setSendingEmail(false);
                setLoading(false);
                setLoadingProgress(0);
            });
    }

    const processPrintReceipt = (): Promise<void> => {
        if (!kioskSettings.settings.printer) {
            console.log("No printer configured, showing receipt window only");
            return Promise.resolve();
        }

        const receiptContent = reactToPrintContent();
        
        const pdfOptions = isDevelopment() ? {
            margin: {
                left: "4mm",
                right: "0mm"
            },
            scale: 0.8 
        } : {
            margin: {
                left: "4mm",
                right: "0mm"
            }
        };

        return drawDOM(receiptContent, pdfOptions)
            .then((group) => {
                setLoadingProgress(30);
                return exportPDF(group, pdfOptions);
            })
            .then((dataUri) => {
                setLoadingProgress(60);
                return prepareReceipt(dataUri.split(";base64,")[1]);
            })
            .then((receiptData: any) => {
                setLoadingProgress(80);
                receiptData.printSettings.printer = kioskSettings.settings.printer.name;

                return printReceipt(receiptData);
            })
            .then((response: any) => {
                setLoadingProgress(100);
                if (response.data?.success === false) {
                    console.log("Print service response:", response.data.message);
                } else {
                    console.log("Print job dispatched successfully:", response);
                }
                
                setTimeout(() => {
                    setPrinting(false);
                    setLoading(false);
                    setLoadingProgress(0);
                }, 200); 
            })
            .catch((error: any) => {
                console.error("Print error:", error);
                if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                    console.warn("Print service is not available. This is expected in development.");
                }
                setPrinting(false);
                setLoading(false);
                setLoadingProgress(0);
            });
    }

    return (
        <Window title={<AttendTitle />} className={"attend-confirmation-window"} onClose={props.onClose}
            maximizeButton={() => null}
            minimizeButton={() => null}
            initialWidth={600}
            resizable={false}
            modal={true}>
            <div className={"container content"}>
                <ClassDetail classItem={props.classItem} />
            </div>
            <div className={"footer"}>
                <SignOutButton />
                <IdleTimer timerType={IDLE_TIMER_TYPE.SUCCESS_SCREEN} />
                {kioskSettings.settings.printer && <Button className="btn-print" iconClass="btn-print-icon" onClick={onPrint}>Print</Button>}
                {kioskSettings.settings.emailReceiptEnabled && <Button className="btn-email" iconClass="btn-email-icon" onClick={onEmail} themeColor={"primary"}>Email</Button>}
            </div>
            {(printing || sendingEmail) &&
                <Window className={"attend-receipt-window"} onClose={props.onClose}
                    title={false}
                    maximizeButton={() => null}
                    minimizeButton={() => null}
                    initialWidth={600}
                    resizable={false}>
                    <div className="receipt-window-title">
                        {sendingEmail ? "Emailing Receipt" : "Attendance Receipt"}
                    </div>
                    <AttendanceReceipt2 classItem={props.classItem} ref={componentRef} />
                </Window >
            }
            {loading && <XplLoader progress={loadingProgress} />}
        </Window>
    );
}

export default AttendConfirmation